/**
 * Gift card recipient form
 * ------------------------
 * Controls the recipient fields rendered by snippets/gift-card-recipient-form.liquid
 * on gift card product pages, per the Shopify recipient form guidelines:
 * https://shopify.dev/docs/storefronts/themes/product-merchandising/gift-cards
 *
 * - Shows/hides the recipient fields when the "send as a gift" checkbox is toggled.
 * - Disables the recipient inputs while hidden, so blank properties aren't sent to the cart.
 * - Fills in the customer's timezone offset for the optional "Send on" schedule date.
 * - Blocks the add-to-cart submission (before the theme's own add-to-cart handler runs)
 *   when the recipient email is required but missing or invalid.
 */
(function () {
  'use strict';

  function pad(number) {
    return String(number).padStart(2, '0');
  }

  function todayAsDateInputValue() {
    var today = new Date();
    return today.getFullYear() + '-' + pad(today.getMonth() + 1) + '-' + pad(today.getDate());
  }

  function toggleRecipientFields(wrapper) {
    var checkbox = wrapper.querySelector('[data-recipient-checkbox]');
    var fields = wrapper.querySelector('[data-recipient-fields]');

    if (!checkbox || !fields) return;

    var checked = checkbox.checked;

    // "is-open" drives the reveal animation (see the .gift-card-recipient__fields
    // rules in assets/custom.css, timed to match this theme's own .collapsible-content
    // easing). Fields stay in the DOM either way; disabling them below is what keeps
    // their properties out of the add-to-cart request while collapsed.
    fields.classList.toggle('is-open', checked);

    var controls = fields.querySelectorAll('input, textarea');
    controls.forEach(function (control) {
      control.disabled = !checked;
    });

    var emailInput = fields.querySelector('[data-recipient-email]');
    var emailError = fields.querySelector('[data-recipient-email-error]');

    if (emailInput) {
      if (checked) {
        emailInput.setAttribute('required', 'required');
      } else {
        emailInput.removeAttribute('required');
        emailInput.removeAttribute('aria-invalid');
        emailInput.classList.remove('error');
      }
    }

    if (emailError && !checked) {
      emailError.hidden = true;
    }
  }

  function setTimezoneOffset(wrapper) {
    var offsetInput = wrapper.querySelector('[data-recipient-offset]');
    if (offsetInput) {
      offsetInput.value = new Date().getTimezoneOffset();
    }
  }

  function setMinSendOnDate(wrapper) {
    var sendOnInput = wrapper.querySelector('[data-recipient-send-on]');
    if (sendOnInput) {
      sendOnInput.setAttribute('min', todayAsDateInputValue());
    }
  }

  function isRecipientFormValid(wrapper) {
    var checkbox = wrapper.querySelector('[data-recipient-checkbox]');
    if (!checkbox || !checkbox.checked) return true;

    var emailInput = wrapper.querySelector('[data-recipient-email]');
    var emailError = wrapper.querySelector('[data-recipient-email-error]');
    var isValid = !!emailInput && emailInput.value.trim() !== '' && emailInput.checkValidity();

    if (emailError) {
      emailError.hidden = isValid;
    }

    if (emailInput) {
      // Same "error" class the theme's own forms use (see input.error in
      // assets/theme.css, and templates/customers/login.liquid for the pattern).
      emailInput.classList.toggle('error', !isValid);
      emailInput.setAttribute('aria-invalid', String(!isValid));
      if (!isValid) {
        emailInput.focus();
      }
    }

    return isValid;
  }

  function initRecipientForm(wrapper) {
    if (wrapper.hasAttribute('data-recipient-initialized')) return;
    wrapper.setAttribute('data-recipient-initialized', 'true');

    var checkbox = wrapper.querySelector('[data-recipient-checkbox]');
    if (!checkbox) return;

    setTimezoneOffset(wrapper);
    setMinSendOnDate(wrapper);
    toggleRecipientFields(wrapper);

    checkbox.addEventListener('change', function () {
      toggleRecipientFields(wrapper);
    });
  }

  function initAll(context) {
    (context || document)
      .querySelectorAll('[data-gift-card-recipient-form]')
      .forEach(initRecipientForm);
  }

  initAll();

  // Re-initialize if a recipient form gets injected later (e.g. a quick-view modal
  // that re-renders the product form over AJAX).
  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-gift-card-recipient-form]')) {
            initRecipientForm(node);
          } else if (node.querySelectorAll) {
            initAll(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Intercept the add-to-cart submission for gift card forms before the theme's own
  // submit/AJAX handlers run, by validating during the capturing phase at the document
  // level (capturing listeners on an ancestor always run before listeners bound
  // directly to the form).
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.querySelector !== 'function') return;

      var wrapper = form.querySelector('[data-gift-card-recipient-form]');
      if (!wrapper) return;

      if (!isRecipientFormValid(wrapper)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
})();